import { Check, Pencil, PencilIcon, Save, Trash, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { editSlide } from "../services/editSlide";
import {
  ChartPlaceholder,
  ImagePlaceholder,
  TablePlaceholder,
  TextPlaceholder,
} from "../types";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { Textarea } from "./ui/textarea";
import { toast } from "./ui/use-toast";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { cn } from "../lib/utils";

const IMAGE_TYPES = [
  {
    id: "company_domain",
    name: "company_domain",
  },
  {
    id: "image_url",
    name: "image_url",
  },
  {
    id: "excel_chart",
    name: "excel_chart",
  },
];

const COMPANY_DOMAIN_METADATA_VALUES = {
  category: [
    {
      id: "audience-company-logo",
      name: "audience-company-logo",
    },
    {
      id: "user-company-logo",
      name: "user-company-logo",
    },
  ],
  ideal_theme: [
    {
      id: "default",
      name: "default",
    },
    {
      id: "dark",
      name: "dark",
    },
    {
      id: "light",
      name: "light",
    },
  ],
  ideal_type: [
    {
      id: "default",
      name: "default",
    },
    {
      id: "logo",
      name: "logo",
    },
    {
      id: "icon",
      name: "icon",
    },
  ],
} as const;

export function EditElement(props: {
  textPlaceholders: Array<TextPlaceholder>;
  imagePlaceholders: Array<ImagePlaceholder>;
  tablePlaceholders: Array<TablePlaceholder>;
  chartPlaceholders: Array<ChartPlaceholder>;
  selectedSlide: any;
  selectedSlideObj: any;
  editDescPortal: any;
  setEditDescPortal: any;
  fullDocument: any;
  newElement: any;
  setShowSelectType: any;
  organizationId: string;
  closeSlidePopup: () => void;
  editSlide: (...args: any) => Promise<any>;
  getPreview: (google_slide_id: string) =>
    | {
        google_slide_id: string;
        s3_preview_url: string;
      }
    | undefined;
}) {
  const [textPlaceholders, setTextPlaceholders] = useState<
    Array<TextPlaceholder>
  >(props.textPlaceholders);

  const [imagePlaceholders, setImagePlaceholders] = useState<
    Array<ImagePlaceholder>
  >(props.imagePlaceholders);

  const [tablePlaceholders, setTablePlaceholders] = useState<
    Array<TablePlaceholder>
  >(props.tablePlaceholders);

  const [chartPlaceholders, setChartPlaceholders] = useState<
    Array<ChartPlaceholder>
  >(props.chartPlaceholders);

  const newTextElement = textPlaceholders.find(
    (tp) =>
      tp.max_chars === 0 &&
      tp.min_chars === 0 &&
      tp.placeholder === "" &&
      tp.instruction === ""
  );

  const newImageElement = imagePlaceholders.find(
    (tp) => tp.alt_text === "" && tp.type === "company_domain" && tp.url === ""
  );

  const newTableElement = tablePlaceholders.find(
    (tp) =>
      tp.alt_text === "" &&
      tp.num_rows === 0 &&
      tp.num_columns === 0 &&
      tp.table_height === 0 &&
      tp.table_width === 0
  );

  const newChartElement = chartPlaceholders.find(
    (cp) => cp.spreadsheet_google_document_id === "" && cp.prompt === ""
  );

  const [editingTextElement, setEditingTextElement] =
    useState<TextPlaceholder | null>(newTextElement || null);

  const [editingImageElement, setEditingImageElement] =
    useState<ImagePlaceholder | null>(newImageElement || null);

  const [editingTableElement, setEditingTableElement] =
    useState<TablePlaceholder | null>(newTableElement || null);

  const [editingChartElement, setEditingChartElement] =
    useState<ChartPlaceholder | null>(newChartElement || null);

  const textField = useRef<HTMLInputElement>(null);
  const imageField = useRef<HTMLInputElement>(null);
  const tableField = useRef<HTMLInputElement>(null);
  const chartField = useRef<HTMLInputElement>(null);

  const textRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [focussedOnce, setFocussedOnce] = useState(false);

  useEffect(() => {
    if (focussedOnce) return;
    if (editingTextElement) {
      const container = containerRef.current;
      const text = textRef.current;
      const textFieldElm = textField.current;
      if (!container || !text || !textFieldElm) return;
      textFieldElm.focus();
      container.scrollTo({
        top: text.offsetTop - 10,
        behavior: "smooth",
      });
      setFocussedOnce(true);
    }
    if (editingImageElement) {
      const container = containerRef.current;
      const image = imageRef.current;
      const imageFieldElm = imageField.current;
      if (!container || !image || !imageFieldElm) return;
      imageFieldElm.focus();
      container.scrollTo({
        top: image.offsetTop - 10,
        behavior: "smooth",
      });
      setFocussedOnce(true);
    }
    if (editingTableElement) {
      const container = containerRef.current;
      const table = tableRef.current;
      const tableFieldElm = tableField.current;
      if (!container || !table || !tableFieldElm) return;
      tableFieldElm.focus();
      container.scrollTo({
        top: table.offsetTop - 10,
        behavior: "smooth",
      });
      setFocussedOnce(true);
    }
    if (editingChartElement) {
      const container = containerRef.current;
      const chart = chartRef.current;
      const chartFieldElm = chartField.current;
      console.log(container, chart, chartFieldElm);

      if (!container || !chart || !chartFieldElm) return;
      chartFieldElm.focus();
      container.scrollTo({
        top: chart.offsetTop - 10,
        behavior: "smooth",
      });
      setFocussedOnce(true);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    editingTextElement,
    editingImageElement,
    editingTableElement,
    editingChartElement,
  ]);

  console.log(props.selectedSlideObj);

  return (
    <div
      className="bg-white absolute right-0 top-2 h-[98vh] rounded-l-lg w-3/4 py-5 px-10 overflow-y-scroll slide-in"
      onClick={(e) => e.stopPropagation()}
      ref={containerRef}
      style={{ transform: "translateY(-50%)" }}
    >
      <div className="flex items-center">
        <div
          className="flex items-center justify-center p-1 rounded-md hover:bg-zinc-100 transition-all"
          role="button"
          onClick={props.closeSlidePopup}
        >
          <X size={15} />
        </div>
      </div>

      <div className="my-3 flex items-center gap-3">
        <img
          className="max-w-[250px] rounded-md border border-zinc-300"
          src={
            props.getPreview(
              props.selectedSlideObj.format_data.google_slide_id ||
                props.selectedSlideObj.id
            )?.s3_preview_url || ""
          }
          alt="slide preview"
        />
        <div className="max-w-[500px]">
          {props.editDescPortal ? (
            <div className="flex items-center gap-3">
              <Textarea
                value={props.editDescPortal}
                onChange={(e) => {
                  props.setEditDescPortal(e.target.value);
                }}
                cols={60}
                rows={10}
              />
              <button
                type="button"
                className="text-green-500 p-2 rounded-md transition-all bg-green-100 hover:bg-green-200"
                onClick={async () => {
                  if (!props.selectedSlide) return;
                  if (
                    props.editDescPortal === props.selectedSlideObj.description
                  ) {
                    props.setEditDescPortal("");
                    return;
                  }
                  toast({
                    title: "Updating Slide",
                    description: "Saving slide description",
                  });
                  await editSlide(props.selectedSlide, {
                    description: props.editDescPortal,
                  });
                  toast({
                    title: "Update Slide",
                    description: "Slide updated successfully",
                  });
                  props.setEditDescPortal("");
                  props.setEditDescPortal("");

                  props.setEditDescPortal("");

                  await props.fullDocument.refetch();
                }}
              >
                <div className="hidden">Save</div>
                <Check size={20} />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-3 justify-start">
              <p className="text-zinc-700">
                {props.selectedSlideObj.description.substring(0, 250) + "..."}
              </p>

              <button
                onClick={async () => {
                  props.setEditDescPortal(props.selectedSlideObj.description);
                }}
              >
                <Pencil size={15} />
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-end my-4">
        <Button
          onClick={async () => {
            props.setShowSelectType(true);
          }}
        >
          Add New Element
        </Button>
      </div>

      <div>
        <div ref={textRef} className="flex items-center gap-2 mb-5">
          <img src="/text.png" alt="text" className="w-6 h-6" />
          <h4 className="text-zinc-600">Text Elements</h4>
        </div>
        <div>
          <Table className="border rounded-md border-zinc-300">
            <TableHeader className="bg-zinc-100">
              <TableRow>
                <TableHead className="">Placeholder ID</TableHead>
                <TableHead className="">Instruction</TableHead>
                <TableHead className="">Min Length</TableHead>
                <TableHead className="">Max Length</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody className="">
              {textPlaceholders
                .map((p) => {
                  return {
                    ...p,
                    isEditing:
                      (editingTextElement && editingTextElement.id === p.id) ||
                      false,
                  };
                })
                .map((placeholder, i: number) => (
                  <TableRow
                    key={i}
                    className={cn(
                      placeholder.isEditing ? "gradient hover:gradient" : ""
                    )}
                  >
                    <TableCell>
                      <div>
                        {placeholder.isEditing ? (
                          <Input
                            ref={textField}
                            type="text"
                            value={
                              editingTextElement?.placeholder ||
                              placeholder.placeholder
                            }
                            onChange={(e) => {
                              setEditingTextElement((f) => {
                                if (!f) return null;
                                f = {
                                  ...f,
                                  placeholder: e.target.value,
                                };
                                return f;
                              });
                            }}
                          />
                        ) : (
                          placeholder.placeholder
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        {placeholder.isEditing ? (
                          <Textarea
                            value={
                              editingTextElement?.instruction ||
                              placeholder.instruction
                            }
                            onChange={(e) => {
                              setEditingTextElement((f) => {
                                if (!f) return null;
                                f = {
                                  ...f,
                                  instruction: e.target.value,
                                };
                                return f;
                              });
                            }}
                          />
                        ) : (
                          placeholder.instruction
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {placeholder.isEditing ? (
                        <Input
                          className="w-[100px]"
                          type="number"
                          value={
                            editingTextElement
                              ? editingTextElement?.min_chars
                              : placeholder.min_chars
                          }
                          onChange={(e) => {
                            setEditingTextElement((f) => {
                              if (!f) return null;
                              if (e.target.value === "") {
                                e.target.value = "0";
                              }
                              if (e.target.value.startsWith("0")) {
                                e.target.value = e.target.value.slice(1);
                              }
                              f = {
                                ...f,
                                min_chars: Number(e.target.value),
                              };
                              return f;
                            });
                          }}
                        />
                      ) : (
                        placeholder.min_chars
                      )}
                    </TableCell>
                    <TableCell>
                      {placeholder.isEditing ? (
                        <Input
                          className="w-[100px]"
                          type="number"
                          value={
                            editingTextElement
                              ? editingTextElement?.max_chars
                              : placeholder.max_chars
                          }
                          onChange={(e) => {
                            setEditingTextElement((f) => {
                              if (!f) return null;
                              if (e.target.value === "") {
                                e.target.value = "0";
                              }
                              if (e.target.value.startsWith("0")) {
                                e.target.value = e.target.value.slice(1);
                              }
                              f = {
                                ...f,
                                max_chars: Number(e.target.value),
                              };
                              return f;
                            });
                          }}
                        />
                      ) : (
                        placeholder.max_chars
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {placeholder.isEditing ? (
                          <div
                            className="flex gap-2 border px-2 py-1 border-[#0d33f1] bg-[#0d33f1]/10 items-center justify-center p-1 rounded-md hover:bg-zinc-200 transition-all"
                            role="button"
                            onClick={async () => {
                              // get other information about slide
                              const _editingTextElement = {
                                ...editingTextElement,
                              };

                              setEditingTextElement(null);
                              const cp = JSON.parse(
                                JSON.stringify(props.selectedSlideObj)
                              );

                              // if a placeholder with this id already exists, replace it
                              // if it doesn't exist, add it
                              if (!_editingTextElement) return;
                              const index = cp.text_placeholders.findIndex(
                                (t: any) => t.id === _editingTextElement.id
                              );

                              if (index > -1) {
                                cp.text_placeholders[index] =
                                  _editingTextElement;
                              } else {
                                cp.text_placeholders.push(_editingTextElement);
                              }
                              setTextPlaceholders(cp.text_placeholders);
                              const { status } = await editSlide(
                                props.selectedSlide,
                                cp
                              );
                              if (status) {
                                setEditingTextElement(null);
                                toast({
                                  title: "Success",
                                  description: "Slide saved successfully",
                                });
                              } else {
                                toast({
                                  title: "Error",
                                  description: "Failed to save slide",
                                  variant: "destructive",
                                });
                              }

                              props.fullDocument.refetch();
                            }}
                          >
                            <p>Save</p>
                            <Save size={15} />
                          </div>
                        ) : (
                          <div
                            className="flex items-center justify-center p-1 rounded-md hover:bg-zinc-200 transition-all"
                            role="button"
                            onClick={() => setEditingTextElement(placeholder)}
                          >
                            <PencilIcon size={15} />
                          </div>
                        )}
                        <div
                          onClick={async () => {
                            const cp = JSON.parse(
                              JSON.stringify(props.selectedSlideObj)
                            );
                            cp.text_placeholders = cp.text_placeholders.filter(
                              (t: any) => t.id !== placeholder.id
                            );
                            setTextPlaceholders(cp.text_placeholders);
                            try {
                              await editSlide(props.selectedSlide, cp);
                              toast({
                                title: "Success",
                                description: "Slide saved successfully",
                              });
                            } catch (error) {
                              toast({
                                title: "Error",
                                description: "Failed to save slide",
                                variant: "destructive",
                              });
                            }
                          }}
                          className="flex items-center justify-center p-1 rounded-md hover:bg-zinc-200 transition-all"
                        >
                          <Trash size={15} />
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <div>
        <div ref={imageRef} className="flex items-center gap-2 my-5">
          <img src="/image.png" alt="preview" className="w-6 h-6" />
          <h4 className="text-zinc-600">Image Elements</h4>
        </div>
        <div>
          <Table className="border rounded-md border-zinc-300">
            <TableHeader className="bg-zinc-100">
              <TableRow>
                <TableHead className="">Alt Text</TableHead>
                <TableHead className="">Type</TableHead>
                <TableHead className="">Metadata</TableHead>
                <TableHead className="">Chart Sheet</TableHead>
                <TableHead className=""></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody className="">
              {imagePlaceholders
                .map((p) => {
                  return {
                    ...p,
                    isEditing:
                      (editingImageElement &&
                        editingImageElement.id === p.id) ||
                      false,
                  };
                })
                .map((placeholder, i: number) => (
                  <TableRow
                    key={i}
                    className={cn(
                      placeholder.isEditing ? "gradient hover:gradient" : ""
                    )}
                  >
                    <TableCell>
                      <div>
                        {placeholder.isEditing ? (
                          <Input
                            ref={imageField}
                            type="text"
                            value={
                              editingImageElement?.alt_text ||
                              placeholder.alt_text
                            }
                            onChange={(e) => {
                              setEditingImageElement((f) => {
                                if (!f) return null;
                                f = {
                                  ...f,
                                  alt_text: e.target.value,
                                };
                                return f;
                              });
                            }}
                          />
                        ) : (
                          placeholder.alt_text
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        {placeholder.isEditing ? (
                          <Select
                            value={
                              editingImageElement?.type || placeholder.type
                            }
                            onValueChange={(v) => {
                              setEditingImageElement((f) => {
                                if (!f) return null;
                                console.log("updating...", v);

                                f = {
                                  ...f,
                                  type: v,
                                  attributes:
                                    v === "company_domain"
                                      ? {
                                          category: "audience-company-logo",
                                          ideal_theme: "default",
                                          ideal_type: "default",
                                        }
                                      : v === "excel_chart"
                                      ? {}
                                      : {},
                                  chart_sheet_name:
                                    v === "excel_chart" ? "" : null,
                                };
                                return f;
                              });
                            }}
                          >
                            <SelectTrigger className="mt-2">
                              <SelectValue placeholder="Select type" />
                            </SelectTrigger>
                            <SelectContent className="p-1">
                              <SelectGroup>
                                {IMAGE_TYPES.map((d) => (
                                  <SelectItem value={d.id}>
                                    <div className="flex items-center gap-3">
                                      {d.name}
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectGroup>
                            </SelectContent>
                          </Select>
                        ) : (
                          placeholder.type
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {placeholder.isEditing ? (
                        <div className="p-2 rounded-md w-full">
                          {editingImageElement?.type === "company_domain" && (
                            <div className="flex flex-col">
                              {(
                                Object.keys(
                                  COMPANY_DOMAIN_METADATA_VALUES
                                ) as Array<
                                  keyof typeof COMPANY_DOMAIN_METADATA_VALUES
                                >
                              ).map((key) => (
                                <div className="flex items-center gap-2 mb-2">
                                  <p className="text-sm bg-white font-medium text-zinc-600 p-2 rounded-md border">
                                    {key}
                                  </p>
                                  {editingImageElement.type ===
                                  "excel_chart" ? (
                                    <input
                                      value={key}
                                      className="text-sm font-medium text-zinc-600 p-2 rounded-md border"
                                    />
                                  ) : (
                                    <Select
                                      value={
                                        editingImageElement?.attributes?.[key]
                                      }
                                      onValueChange={(v) => {
                                        setEditingImageElement((f) => {
                                          if (!f) return null;
                                          f = {
                                            ...f,
                                            attributes: {
                                              ...f.attributes,
                                              [key]: v,
                                            },
                                          };
                                          return f;
                                        });
                                      }}
                                    >
                                      <SelectTrigger className="">
                                        <SelectValue placeholder="Select type" />
                                      </SelectTrigger>
                                      <SelectContent className="p-1">
                                        <SelectGroup>
                                          {COMPANY_DOMAIN_METADATA_VALUES[
                                            key
                                          ].map((d) => (
                                            <SelectItem value={d.id}>
                                              <div className="flex items-center gap-3">
                                                {d.name}
                                              </div>
                                            </SelectItem>
                                          ))}
                                        </SelectGroup>
                                      </SelectContent>
                                    </Select>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="p-2 rounded-md">
                          <div className="flex flex-col">
                            {Object.keys(placeholder.attributes || {}).map(
                              (key) => (
                                <div className="flex items-center gap-2">
                                  <p className="text-sm font-medium text-zinc-600">
                                    {key}:
                                  </p>
                                  <p className="text-sm text-zinc-500">
                                    {placeholder.attributes?.[key]}
                                  </p>
                                </div>
                              )
                            )}
                          </div>
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {placeholder.id === editingImageElement?.id ? (
                        <>
                          {editingImageElement?.type === "excel_chart" ? (
                            <div className="flex flex-col">
                              <div className="flex items-center justify-start gap-2">
                                <div>
                                  <p className="text-sm bg-white font-medium text-zinc-600 p-2 rounded-md border">
                                    chart_sheet_name
                                  </p>
                                </div>
                                <input
                                  type="text"
                                  className="p-2 bg-white border rounded-md"
                                  value={
                                    editingImageElement?.chart_sheet_name || ""
                                  }
                                  onChange={(e) => {
                                    setEditingImageElement((f) => {
                                      if (!f) return null;
                                      f = {
                                        ...f,
                                        chart_sheet_name: e.target.value,
                                      };
                                      return f;
                                    });
                                  }}
                                />
                              </div>
                            </div>
                          ) : (
                            <div className="flex flex-col"></div>
                          )}
                        </>
                      ) : (
                        <>
                          {placeholder.type === "excel_chart" && (
                            <p>{placeholder.chart_sheet_name}</p>
                          )}
                        </>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {placeholder.isEditing ? (
                          <div
                            className="flex gap-2 border px-2 py-1 border-[#0d33f1] bg-[#0d33f1]/10 items-center justify-center p-1 rounded-md hover:bg-zinc-200 transition-all"
                            role="button"
                            onClick={async () => {
                              // get other information about slide
                              const _editingImageElement = {
                                ...editingImageElement,
                                attributes:
                                  typeof editingImageElement?.attributes ===
                                  "string"
                                    ? JSON.parse(
                                        editingImageElement?.attributes
                                      )
                                    : editingImageElement?.attributes,
                              };

                              // console.log(_editingImageElement);

                              setEditingImageElement(null);
                              const cp = JSON.parse(
                                JSON.stringify(props.selectedSlideObj)
                              );

                              // if a placeholder with this id already exists, replace it
                              // if it doesn't exist, add it
                              if (!_editingImageElement) return;
                              const index = cp.image_placeholders.findIndex(
                                (t: any) => t.id === _editingImageElement.id
                              );

                              if (
                                !_editingImageElement.alt_text ||
                                !_editingImageElement.type
                              ) {
                                toast({
                                  title: "Error",
                                  description: "Alt text and type are required",
                                  variant: "destructive",
                                });
                                return;
                              }

                              if (
                                _editingImageElement.type === "excel_chart" &&
                                !_editingImageElement.chart_sheet_name
                              ) {
                                toast({
                                  title: "Error",
                                  description: "Chart sheet name is required",
                                  variant: "destructive",
                                });
                                return;
                              }

                              if (
                                _editingImageElement.type === "company_domain"
                              ) {
                                if (!_editingImageElement.attributes.category) {
                                  toast({
                                    title: "Error",
                                    description:
                                      "Category is required in metadata",
                                    variant: "destructive",
                                  });
                                  return;
                                }

                                // remove keys with default values from attributes
                                const _attributes = {
                                  ..._editingImageElement.attributes,
                                };
                                for (const key in _attributes) {
                                  if (
                                    _attributes[key] === "default" ||
                                    _attributes[key] === ""
                                  ) {
                                    delete _attributes[key];
                                  }
                                }

                                _editingImageElement.attributes = _attributes;
                              }

                              if (index > -1) {
                                cp.image_placeholders[index] =
                                  _editingImageElement;
                              } else {
                                cp.image_placeholders.push(
                                  _editingImageElement
                                );
                              }
                              setImagePlaceholders(cp.image_placeholders);
                              const { status } = await editSlide(
                                props.selectedSlide,
                                cp
                              );
                              if (status) {
                                setEditingImageElement(null);
                                toast({
                                  title: "Success",
                                  description: "Slide saved successfully",
                                });
                              } else {
                                toast({
                                  title: "Error",
                                  description: "Failed to save slide",
                                  variant: "destructive",
                                });
                              }
                              props.fullDocument.refetch();
                              setEditingImageElement(null);
                            }}
                          >
                            <p>Save</p>
                            <Save size={15} />
                          </div>
                        ) : (
                          <div
                            className="flex items-center justify-center p-1 rounded-md hover:bg-zinc-200 transition-all"
                            role="button"
                            onClick={async () => {
                              setEditingImageElement(placeholder);
                            }}
                          >
                            <PencilIcon size={15} />
                          </div>
                        )}
                        <div className="flex items-center justify-center p-1 rounded-md hover:bg-zinc-200 transition-all">
                          <Trash
                            size={15}
                            onClick={async () => {
                              const cp = JSON.parse(
                                JSON.stringify(props.selectedSlideObj)
                              );
                              cp.image_placeholders =
                                cp.image_placeholders.filter(
                                  (t: any) => t.id !== placeholder.id
                                );
                              setImagePlaceholders(cp.image_placeholders);

                              try {
                                await editSlide(props.selectedSlide, cp);
                                toast({
                                  title: "Success",
                                  description: "Slide saved successfully",
                                });
                              } catch (error) {
                                toast({
                                  title: "Error",
                                  description: "Failed to save slide",
                                  variant: "destructive",
                                });
                              }
                            }}
                          />
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <div>
        <div ref={chartRef} className="flex items-center gap-2 my-5">
          <img src="/area_chart.png" alt="preview" className="w-6 h-6" />
          <h4 className="text-zinc-600">Chart Elements</h4>
        </div>
        <div>
          <Table className="border rounded-md border-zinc-300">
            <TableHeader className="bg-zinc-100">
              <TableRow>
                <TableHead className="">SpreadSheet Document ID</TableHead>
                <TableHead className="">Prompt</TableHead>
                <TableHead className=""></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody className="">
              {chartPlaceholders
                .map((p) => {
                  return {
                    ...p,
                    isEditing:
                      (editingChartElement &&
                        editingChartElement.id === p.id) ||
                      false,
                  };
                })
                .map((placeholder, i: number) => (
                  <TableRow
                    key={i}
                    className={cn(
                      placeholder.isEditing
                        ? "gradient hover:bg-[#d33f115]"
                        : ""
                    )}
                  >
                    <TableCell>
                      <div>
                        {placeholder.isEditing ? (
                          <Input
                            ref={chartField}
                            type="text"
                            value={
                              editingChartElement?.spreadsheet_google_document_id ||
                              placeholder.spreadsheet_google_document_id
                            }
                            onChange={(e) => {
                              setEditingChartElement((f) => {
                                if (!f) return null;
                                f = {
                                  ...f,
                                  spreadsheet_google_document_id:
                                    e.target.value,
                                };
                                return f;
                              });
                            }}
                          />
                        ) : (
                          placeholder.spreadsheet_google_document_id
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        {placeholder.isEditing ? (
                          <Input
                            type="text"
                            value={
                              editingChartElement?.prompt || placeholder.prompt
                            }
                            onChange={(e) => {
                              setEditingChartElement((f) => {
                                if (!f) return null;
                                f = {
                                  ...f,
                                  prompt: e.target.value,
                                };
                                return f;
                              });
                            }}
                          />
                        ) : (
                          placeholder.prompt
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {placeholder.isEditing ? (
                          <div
                            className="flex gap-2 border px-2 py-1 border-[#0d33f1] bg-[#0d33f1]/10 items-center justify-center p-1 rounded-md hover:bg-zinc-200 transition-all"
                            role="button"
                            onClick={async () => {
                              // get other information about slide
                              const _editingChartElement = {
                                ...editingChartElement,
                              };

                              setEditingChartElement(null);
                              const cp = JSON.parse(
                                JSON.stringify(props.selectedSlideObj)
                              );

                              // if a placeholder with this id already exists, replace it
                              // if it doesn't exist, add it
                              if (!_editingChartElement) return;

                              const index = cp.chart_placeholders.findIndex(
                                (t: any) => t.id === _editingChartElement.id
                              );

                              if (index > -1) {
                                cp.chart_placeholders[index] =
                                  _editingChartElement;
                              } else {
                                cp.chart_placeholders.push(
                                  _editingChartElement
                                );
                              }
                              setChartPlaceholders(cp.chart_placeholders);
                              const { status } = await editSlide(
                                props.selectedSlide,
                                cp
                              );
                              if (status) {
                                setEditingChartElement(null);
                                props.fullDocument.refetch();
                                toast({
                                  title: "Success",
                                  description: "Slide saved successfully",
                                });
                              } else {
                                toast({
                                  title: "Error",
                                  description: "Failed to save slide",
                                  variant: "destructive",
                                });
                              }
                            }}
                          >
                            <p>Save</p>
                            <Save size={15} />
                          </div>
                        ) : (
                          <div
                            className="flex items-center justify-center p-1 rounded-md hover:bg-zinc-200 transition-all"
                            role="button"
                            onClick={async () => {
                              setEditingChartElement(placeholder);
                            }}
                          >
                            <PencilIcon size={15} />
                          </div>
                        )}
                        <div className="flex items-center justify-center p-1 rounded-md hover:bg-zinc-200 transition-all">
                          <Trash
                            size={15}
                            onClick={async () => {
                              const cp = JSON.parse(
                                JSON.stringify(props.selectedSlideObj)
                              );
                              cp.chart_placeholders =
                                cp.chart_placeholders.filter(
                                  (t: any) => t.id !== placeholder.id
                                );
                              setChartPlaceholders(cp.chart_placeholders);
                              const { status } = await editSlide(
                                props.selectedSlide,
                                cp
                              );
                              if (status) {
                                setEditingChartElement(null);
                                props.fullDocument.refetch();
                                toast({
                                  title: "Success",
                                  description: "Slide saved successfully",
                                });
                              } else {
                                toast({
                                  title: "Error",
                                  description: "Failed to save slide",
                                  variant: "destructive",
                                });
                              }
                            }}
                          />
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <div>
        <div ref={tableRef} className="flex items-center gap-2 my-5">
          <img src="/hub.png" alt="preview" className="w-6 h-6" />
          <h4 className="text-zinc-600">Table Elements</h4>
        </div>
        <div>
          <Table className="border rounded-md border-zinc-300">
            <TableHeader className="bg-zinc-100">
              <TableRow>
                <TableHead className="">Alt Text</TableHead>
                <TableHead className="">Table Rows</TableHead>
                <TableHead className="">Table Columns</TableHead>
                <TableHead className="">Table Height</TableHead>
                <TableHead className="">Table Width</TableHead>
                <TableHead className=""></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody className="">
              {tablePlaceholders
                .map((p) => {
                  return {
                    ...p,
                    isEditing:
                      (editingTableElement &&
                        editingTableElement.id === p.id) ||
                      false,
                  };
                })
                .map((placeholder, i: number) => (
                  <TableRow
                    key={i}
                    className={cn(
                      placeholder.isEditing
                        ? "gradient hover:bg-[#d33f115]"
                        : ""
                    )}
                  >
                    <TableCell>
                      <div>
                        {placeholder.isEditing ? (
                          <Input
                            ref={tableField}
                            type="text"
                            value={
                              editingTableElement?.alt_text ||
                              placeholder.alt_text
                            }
                            onChange={(e) => {
                              setEditingTableElement((f) => {
                                if (!f) return null;
                                f = {
                                  ...f,
                                  alt_text: e.target.value,
                                };
                                return f;
                              });
                            }}
                          />
                        ) : (
                          placeholder.alt_text
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        {placeholder.isEditing ? (
                          <Input
                            type="number"
                            value={
                              editingTableElement?.num_rows ||
                              placeholder.num_rows
                            }
                            onChange={(e) => {
                              setEditingTableElement((f) => {
                                if (!f) return null;
                                let value = 0;
                                try {
                                  value = parseInt(e.target.value, 10);
                                  if (isNaN(value) || value < 0) {
                                    value = 0;
                                  }
                                } catch {
                                  value = 0;
                                }
                                f = {
                                  ...f,
                                  num_rows: value,
                                };
                                return f;
                              });
                            }}
                          />
                        ) : (
                          placeholder.num_rows
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        {placeholder.isEditing ? (
                          <Input
                            type="number"
                            value={
                              editingTableElement?.num_columns ||
                              placeholder.num_columns
                            }
                            onChange={(e) => {
                              setEditingTableElement((f) => {
                                if (!f) return null;
                                let value = 0;
                                try {
                                  value = parseInt(e.target.value, 10);
                                  if (isNaN(value) || value < 0) {
                                    value = 0;
                                  }
                                } catch {
                                  value = 0;
                                }
                                f = {
                                  ...f,
                                  num_columns: value,
                                };
                                return f;
                              });
                            }}
                          />
                        ) : (
                          placeholder.num_columns
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        {placeholder.isEditing ? (
                          <Input
                            type="number"
                            value={
                              editingTableElement?.table_height ||
                              placeholder.table_height
                            }
                            onChange={(e) => {
                              setEditingTableElement((f) => {
                                if (!f) return null;
                                let value = 0;
                                try {
                                  value = parseInt(e.target.value, 10);
                                  if (isNaN(value) || value < 0) {
                                    value = 0;
                                  }
                                } catch {
                                  value = 0;
                                }
                                f = {
                                  ...f,
                                  table_height: value,
                                };
                                return f;
                              });
                            }}
                          />
                        ) : (
                          placeholder.table_height
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        {placeholder.isEditing ? (
                          <Input
                            type="number"
                            value={
                              editingTableElement?.table_width ||
                              placeholder.table_width
                            }
                            onChange={(e) => {
                              setEditingTableElement((f) => {
                                if (!f) return null;
                                let value = 0;
                                try {
                                  value = parseInt(e.target.value, 10);
                                  if (isNaN(value) || value < 0) {
                                    value = 0;
                                  }
                                } catch {
                                  value = 0;
                                }
                                f = {
                                  ...f,
                                  table_width: value,
                                };
                                return f;
                              });
                            }}
                          />
                        ) : (
                          placeholder.table_width
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {placeholder.isEditing ? (
                          <div
                            className="flex gap-2 border px-2 py-1 border-[#0d33f1] bg-[#0d33f1]/10 items-center justify-center p-1 rounded-md hover:bg-zinc-200 transition-all"
                            role="button"
                            onClick={async () => {
                              // get other information about slide
                              const _editingTableElement = {
                                ...editingTableElement,
                              };

                              setEditingTableElement(null);
                              const cp = JSON.parse(
                                JSON.stringify(props.selectedSlideObj)
                              );

                              // if a placeholder with this id already exists, replace it
                              // if it doesn't exist, add it
                              if (!_editingTableElement) return;
                              // console.log(
                              //   _editingTableElement,
                              //   cp.table_placeholders,
                              //   props.selectedSlideObj
                              // );

                              const index = cp.table_placeholders.findIndex(
                                (t: any) => t.id === _editingTableElement.id
                              );

                              if (index > -1) {
                                cp.table_placeholders[index] =
                                  _editingTableElement;
                              } else {
                                cp.table_placeholders.push(
                                  _editingTableElement
                                );
                              }

                              console.log(
                                "cp.table_placeholders",
                                cp.table_placeholders
                              );

                              setTablePlaceholders(cp.table_placeholders);
                              const { status } = await editSlide(
                                props.selectedSlide,
                                cp
                              );
                              if (status) {
                                setEditingTableElement(null);
                                toast({
                                  title: "Success",
                                  description: "Slide saved successfully",
                                });
                              } else {
                                toast({
                                  title: "Error",
                                  description: "Failed to save slide",
                                  variant: "destructive",
                                });
                              }
                            }}
                          >
                            <p>Save</p>
                            <Save size={15} />
                          </div>
                        ) : (
                          <div
                            className="flex items-center justify-center p-1 rounded-md hover:bg-zinc-200 transition-all"
                            role="button"
                            onClick={async () => {
                              setEditingTableElement(placeholder);
                            }}
                          >
                            <PencilIcon size={15} />
                          </div>
                        )}
                        <div className="flex items-center justify-center p-1 rounded-md hover:bg-zinc-200 transition-all">
                          <Trash
                            size={15}
                            onClick={async () => {
                              const cp = JSON.parse(
                                JSON.stringify(props.selectedSlideObj)
                              );
                              cp.table_placeholders =
                                cp.table_placeholders.filter(
                                  (t: any) =>
                                    t.alt_text !== placeholder.alt_text
                                );
                              setTablePlaceholders(cp.table_placeholders);
                              const { status } = await editSlide(
                                props.selectedSlide,
                                cp
                              );
                              if (status) {
                                setEditingTableElement(null);
                                toast({
                                  title: "Success",
                                  description: "Slide saved successfully",
                                });
                              } else {
                                toast({
                                  title: "Error",
                                  description: "Failed to save slide",
                                  variant: "destructive",
                                });
                              }
                            }}
                          />
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
